import React, { useEffect, useMemo, useState } from 'react';
import BlogCard from './BlogCard';
import SeoHelmet from "../components/SeoHelmet";
import ContactModal from "../components/ContactModal";
import slide1 from '../assets/img/blog_banner.jpg';

// ===== CONFIG =====
const API_BASE = "https://workdayz.in/formdata";
const API_LIST = `${API_BASE}/api_blog_list.php`;

// Date formatter
const prettyDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso || "";
  }
};

// API → BlogCard mapper
const mapRow = (row) => ({
  id: row.id,
  title: row.title,
  excerpt: row.excerpt || "",
  image: row.image
    ? row.image
    : row.cover_path
    ? `${API_BASE}/${row.cover_path}`
    : "",
  author: row.author ? `By ${row.author}` : "By admin",
  date: prettyDate(row.created_at || row.date),
  link: `/blog/${row.slug}`,
});

const Blog = () => {
  const postsPerPage = 9;

  const [currentPage, setCurrentPage] = useState(1);
  const [posts, setPosts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // ===== FETCH BLOGS =====
  useEffect(() => {
    const controller = new AbortController();

    const loadBlogs = async () => {
      setLoading(true);
      setErr("");

      try {
        const url = new URL(API_LIST);
        url.searchParams.set("page", currentPage);
        url.searchParams.set("limit", postsPerPage);

        const res = await fetch(url, { signal: controller.signal });
        const json = await res.json();

        if (!json?.success) throw new Error("API failed");

        setPosts((json.data || []).map(mapRow));
        setTotalPages(json.total_pages || 1);
      } catch (e) {
        if (e.name !== "AbortError") {
          setErr("Failed to load blogs.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
    return () => controller.abort();
  }, [currentPage]);

  // ===== 3 POSTS PER ROW (1 BIG + 2 SMALL) =====
  const chunkedPosts = useMemo(() => {
    const chunks = [];
    for (let i = 0; i < posts.length; i += 3) {
      chunks.push(posts.slice(i, i + 3));
    }
    return chunks;
  }, [posts]);

  return (
    <>
      <SeoHelmet />
      <ContactModal />

      {/* Banner */}
      <section>
        <div
          className="design_banner"
          style={{ backgroundImage: `url(${slide1})` }}
        >
          <div className="container">
            <h2 style={{ color: '#fff', padding: '120px 0 80px' }}>Blog</h2>
          </div>
        </div>
      </section>

      {/* Blog List */}
      <section className="blog">
        <div className="container">

          {loading && (
            <div style={{ textAlign: 'center', padding: 30 }}>Loading…</div>
          )}

          {err && (
            <div style={{ textAlign: 'center', color: 'tomato', padding: 30 }}>
              {err}
            </div>
          )}

          {!loading && !err && chunkedPosts.map((chunk, index) => {
            const [big, s1, s2] = chunk;
            const even = index % 2 === 0;

            return (
              <div className="row blogg" key={big.id}>
                {even ? (
                  <>
                    <div className="col-sm-8">
                      <BlogCard post={big} height="690px" />
                    </div>
                    <div className="col-sm-4 colrevmbr">
                      {s1 && <BlogCard post={s1} height="330px" style={{ marginBottom: 30 }} />}
                      {s2 && <BlogCard post={s2} height="330px" />}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-sm-4 colrevmbr">
                      {s1 && <BlogCard post={s1} height="330px" style={{ marginBottom: 30 }} />}
                      {s2 && <BlogCard post={s2} height="330px" />}
                    </div>
                    <div className="col-sm-8">
                      <BlogCard post={big} height="690px" />
                    </div>
                  </>
                )}
              </div>
            );
          })}

          {/* ===== PAGINATION ===== */}
          {!loading && !err && totalPages > 1 && (
            <div style={{ textAlign: 'center', marginTop: 40 }}>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  style={{
                    margin: '0 6px 20px',
                    padding: '6px 12px',
                    background: 'transparent',
                    color: '#222',
                    border: 'none',
                    borderBottom:
                      currentPage === i + 1
                        ? '2px solid #0B3568'
                        : '2px solid transparent',
                    fontWeight: currentPage === i + 1 ? 'bold' : 'normal',
                    cursor: 'pointer',
                  }}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}

        </div>
      </section>
    </>
  );
};

export default Blog;
